import React, { useState, useEffect, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import { Button } from "react-bootstrap";
import LazyImage from "./LazyImage";
import Confetti from "react-confetti";
import { useNavigate } from "react-router-dom";
import { grantPromo, getAddress } from "../Services/Service";

const DEFAULT_ADDRESS_ID = "TXpQOUFpa2V2eFA0WFlITjA4U092Zz09";

const PromoPopup = ({
  show,
  onHide,
  promoData,
  autoClaim,
  onAutoClaimComplete,
}) => {
  const navigate = useNavigate();
  const [claimingPromoId, setClaimingPromoId] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [claimedPromos, setClaimedPromos] = useState(new Set());
  const [addressId, setAddressId] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successOverlay, setSuccessOverlay] = useState({
    visible: false,
    promoName: "",
  });
  const [autoClaimTriggered, setAutoClaimTriggered] = useState(false);
  const [forceRegisterView, setForceRegisterView] = useState(false);
  const MAX_DESC_LENGTH = 90;

  useEffect(() => {
    const registeredFlag = localStorage.getItem("alreadyRegistered");
    if (registeredFlag === "N") {
      setForceRegisterView(true);
    } else {
      setForceRegisterView(false);
    }

    if (show) {
      // Fetch user's address when popup opens
      const userId = localStorage.getItem("auth");
      if (userId) {
        getAddress(userId)
          .then((resp) => {
            if (resp.data && resp.data.data && resp.data.data.length > 0) {
              // Get default address or first address
              const defaultAddress = resp.data.data.find(
                (addr) => addr.isDefault === "Y"
              );
              const address = defaultAddress || resp.data.data[0];
              if (address && address.addressId) {
                setAddressId(address.addressId);
              }
            }
          })
          .catch((err) => {
            console.error("Error fetching address:", err);
          });
      }
    } else {
      // Reset states when popup closes
      setClaimingPromoId(null);
      setErrorMessage(null);
      if (!successOverlay.visible) {
        setShowConfetti(false);
      }
      setAutoClaimTriggered(false);
    }
  }, [show, successOverlay.visible]);

  const handleClaim = useCallback(
    async (promo, options = {}) => {
      const { isAuto = false } = options;
      const promoId = promo.id;
      const userId = localStorage.getItem("auth");
      if (!userId) {
        setErrorMessage("Please login to claim rewards");
        setTimeout(() => setErrorMessage(null), 3000);
        if (isAuto && typeof onAutoClaimComplete === "function") {
          onAutoClaimComplete();
        }
        return;
      }

      const targetAddressId = addressId || DEFAULT_ADDRESS_ID;

      setClaimingPromoId(promoId);
      setErrorMessage(null);

      try {
        const response = await grantPromo(promoId, targetAddressId);
        if (response.data && response.data.code === 200) {
          // Success - show celebration
          setClaimedPromos((prev) => new Set([...prev, promoId]));
          setShowConfetti(true);
          setSuccessOverlay({
            visible: true,
            promoName: promo.name || "Your promo",
          });
          if (typeof onHide === "function") {
            onHide();
          }
          setTimeout(() => {
            setShowConfetti(false);
          }, 5000); // Show confetti for 5 seconds
        } else {
          // Handle error response
          const errorMsg =
            response.data?.message ||
            "Failed to claim reward. Please try again.";
          setErrorMessage(errorMsg);
          setTimeout(() => setErrorMessage(null), 3000);
        }
      } catch (error) {
        console.error("Error claiming promo:", error);
        const errorMsg =
          error.response?.data?.message ||
          error.message ||
          "Failed to claim reward. Please try again.";
        setErrorMessage(errorMsg);
        setTimeout(() => setErrorMessage(null), 3000);
      } finally {
        setClaimingPromoId(null);
        if (isAuto && typeof onAutoClaimComplete === "function") {
          onAutoClaimComplete();
        }
      }
    },
    [addressId, onHide, onAutoClaimComplete]
  );
  // Trigger auto claim once when requested
  useEffect(() => {
    if (
      show &&
      autoClaim &&
      !autoClaimTriggered &&
      promoData &&
      promoData.length > 0
    ) {
      setAutoClaimTriggered(true);
      handleClaim(promoData[0], { isAuto: true });
    }
  }, [show, autoClaim, autoClaimTriggered, promoData, handleClaim]);

  const parseProductImages = (imageString) => {
    if (!imageString) return [];
    try {
      return JSON.parse(imageString);
    } catch (error) {
      return [imageString];
    }
  };

  if (!promoData || !promoData.length) {
    return null;
  }

  if (forceRegisterView) {
    return (
      <Modal
        show={show}
        onHide={onHide}
        centered
        dialogClassName="promo-modal"
        size="lg"
      >
        <div className="bg-light text-white rounded-3 shadow-lg w-100 p-4 text-center">
          <h2 className="fw-bold text-warning mb-3">
            Register To Unlock Rewards
          </h2>
          <p className="mb-4">
            Complete your registration to claim these exclusive rewards waiting
            for you.
          </p>
          {promoData.map((promo, index) => (
            <div
              key={promo.id || index}
              className="mb-4 p-3 bg-black bg-opacity-25 rounded-3 border border-secondary text-start"
            >
              <h5 className="fw-bold text-warning mb-2">{promo.name}</h5>
              <p className="text-light small mb-3">{promo.description}</p>
              {promo.items && promo.items.length > 0 && (
                <div className="d-grid gap-2">
                  {promo.items.map((item, itemIndex) => (
                    <div
                      key={item.id || itemIndex}
                      className="p-2 bg-dark bg-opacity-50 rounded-2 border border-secondary"
                    >
                      {item.itemType === "points" ? (
                        <div className="d-flex align-items-center">
                          <span className="fs-5 me-2">💰</span>
                          <div className="d-flex align-items-center">
                            <LazyImage
                              src={"/assets/images/spicegold.png"}
                              alt="SpiceGold"
                              style={{
                                width: "20px",
                                height: "20px",
                                marginRight: "8px",
                              }}
                            />
                            <span className="fw-bold me-2">
                              {item.quantity} SpiceGold
                            </span>
                          </div>
                        </div>
                      ) : item.itemType === "product" && item.product ? (
                        <div>
                          <div className="d-flex align-items-start mb-2">
                            {parseProductImages(item.product.productImage)
                              .length > 0 && (
                              <LazyImage
                                src={
                                  parseProductImages(
                                    item.product.productImage
                                  )[0]
                                }
                                alt={item.product.productTitle}
                                className="rounded me-3"
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  objectFit: "cover",
                                }}
                              />
                            )}
                            <div className="flex-grow-1">
                              <div className="fw-bold mb-1">
                                {item.product.productTitle}
                              </div>
                              <div className="small text-light mb-1">
                                {item.product.productDesc &&
                                item.product.productDesc.length >
                                  MAX_DESC_LENGTH
                                  ? `${item.product.productDesc.substring(
                                      0,
                                      MAX_DESC_LENGTH
                                    )}...`
                                  : item.product.productDesc}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center">
                          <span className="fs-5 me-2">🎁</span>
                          <span>
                            {item.itemType}: {item.quantity}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          <Button
            variant="warning"
            className="fw-bold px-4 py-2"
            onClick={() => onHide()}
          >
            Close
          </Button>
        </div>
      </Modal>
    );
  }
  console.log(forceRegisterView, "forceRegisterView");

  return (
    <>
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          numberOfPieces={320}
          recycle={false}
          style={{ position: "fixed", top: 0, left: 0, zIndex: 1055 }}
        />
      )}

      {successOverlay.visible && (
        <div className="promo-success-overlay">
          <div className="promo-success-card">
            <div className="promo-success-icon">🎉</div>
            <h2>Congratulations!</h2>
            <p>
              You have successfully claimed the{" "}
              <span className="text-highlight">{successOverlay.promoName}</span>{" "}
              rewards.
            </p>
            <div className="promo-success-actions">
              <Button
                variant="light"
                className="fw-bold px-4"
                onClick={() => {
                  setSuccessOverlay({ visible: false, promoName: "" });
                  setShowConfetti(false);
                }}
              >
                Continue Exploring
              </Button>
            </div>
          </div>
        </div>
      )}

      <Modal
        show={show}
        onHide={onHide}
        centered
        dialogClassName="promo-modal"
        size="lg"
      >
        <div className="bg-light text-white rounded-3 shadow-lg w-100">
          <Modal.Header
            closeButton
            className="border-bottom-0 p-3 pb-2"
            closeVariant="white"
            style={{ color: "white" }}
          >
            <Modal.Title className="fw-bolder fs-4">
              Rewards For You
            </Modal.Title>
          </Modal.Header>

          <Modal.Body
            className="promo-modal__body p-3 pt-0"
            style={{
              color: "white",
              lineHeight: "1.6",
              maxHeight: "70vh",
              overflowY: "auto",
            }}
          >
            {promoData.map((promo, index) => (
              <div
                key={promo.id || index}
                className="promo-modal__reward-card mb-0 p-2  bg-opacity-25 rounded-3"
              >
                {/* Promo Header */}
                <div className="mb-0">
                  <p className="small text-light">{promo.description}</p>
                </div>

                {/* Promo Items */}
                {promo.items && promo.items.length > 0 && (
                  <div className="mt-2">
                    <h6 className="fw-bold mb-2 text-warning">Rewards:</h6>
                    <div className="d-grid gap-2">
                      {promo.items.map((item, itemIndex) => (
                        <div
                          key={item.id || itemIndex}
                          className="promo-modal__reward-item p-2 bg-dark bg-opacity-50 rounded-2 border border-secondary"
                        >
                          {item.itemType === "points" ? (
                            <div className="d-flex align-items-center">
                              <span className="fs-5 me-2">💰</span>
                              <div className="d-flex align-items-center">
                                <LazyImage
                                  src={"/assets/images/spicegold.png"}
                                  alt="SpiceGold"
                                  style={{
                                    width: "20px",
                                    height: "20px",
                                    marginRight: "8px",
                                  }}
                                />
                                <span className="fw-bold me-2">
                                  {item.quantity} SpiceGold
                                </span>
                              </div>
                            </div>
                          ) : item.itemType === "product" && item.product ? (
                            <div>
                              <div className="d-flex align-items-start mb-2">
                                {parseProductImages(item.product.productImage)
                                  .length > 0 && (
                                  <LazyImage
                                    src={
                                      parseProductImages(
                                        item.product.productImage
                                      )[0]
                                    }
                                    alt={item.product.productTitle}
                                    className="rounded me-3"
                                    style={{
                                      width: "60px",
                                      height: "60px",
                                      objectFit: "cover",
                                    }}
                                  />
                                )}
                                <div className="flex-grow-1">
                                  <div className="fw-bold mb-1">
                                    {item.product.productTitle}
                                  </div>
                                  <div className="small text-light mb-1">
                                    {item.product.productDesc &&
                                    item.product.productDesc.length >
                                      MAX_DESC_LENGTH ? (
                                      <>
                                        {item.product.productDesc.substring(
                                          0,
                                          MAX_DESC_LENGTH
                                        )}
                                        ...{" "}
                                        <span
                                          onClick={() => {
                                            onHide();
                                            navigate(
                                              `/productdetails/${item.product.productId}`
                                            );
                                          }}
                                          style={{
                                            color: "red",
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            fontWeight: "bold",
                                          }}
                                        >
                                          Read more
                                        </span>
                                      </>
                                    ) : (
                                      item.product.productDesc
                                    )}
                                  </div>
                                  <div className="d-flex flex-wrap gap-2 align-items-center">
                                    <span className="badge bg-primary">
                                      Qty: &nbsp;&nbsp; {item.quantity}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="d-flex align-items-center">
                              <span className="fs-5 me-2">🎁</span>
                              <span>
                                {item.itemType}: {item.quantity}
                              </span>
                              {item.productId && (
                                <span className="ms-2 small text-muted">
                                  (Product ID: {item.productId})
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </Modal.Body>

          {/* Error Message */}
          {errorMessage && (
            <div className="px-3 pb-2">
              <div className="alert alert-danger mb-0 promo-alert" role="alert">
                {errorMessage}
              </div>
            </div>
          )}

          <Modal.Footer className="promo-modal__footer border-top-0 p-3 pt-0">
            <div className="promo-modal__actions w-100">
              {promoData.map((promo, index) => (
                <div key={promo.id || index} className="mb-2">
                  {claimedPromos.has(promo.id) ? (
                    <div
                      className="alert alert-success mb-0 promo-alert"
                      role="alert"
                    >
                      <strong>
                        🎉 {promo.name} - Rewards Claimed Successfully!
                      </strong>
                    </div>
                  ) : (
                    <Button
                      variant="warning"
                      className="promo-modal__primary-btn fw-bold px-4 py-2 w-100"
                      onClick={() => handleClaim(promo)}
                      disabled={claimingPromoId === promo.id}
                    >
                      {claimingPromoId === promo.id ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Claiming {promo.name}...
                        </>
                      ) : (
                        `Claim Your Rewards`
                      )}
                    </Button>
                  )}
                </div>
              ))}
              <div className="mt-3 text-center">
                <Button
                  variant="secondary"
                  className="promo-modal__secondary-btn fw-bold px-4 py-2"
                  onClick={onHide}
                >
                  Close
                </Button>
              </div>
            </div>
          </Modal.Footer>
        </div>
      </Modal>
    </>
  );
};

export default PromoPopup;
